/**
 * After Data360 pass mapping completes, pull sample timestamps and write
 * derived fsTimes onto the matching Floor Sheet weld row.
 */

import {
  deriveFloorSheetFsTimesFromSamples,
  mergeDerivedFsTimes,
  type DeriveFloorSheetFsTimesResult,
  type FloorSheetFsTimesLogSnapshot
} from './deriveFloorSheetFsTimes'
import { normalizeFsTimes, type FloorSheetFsTimes } from './floorSheetFsTimes'
import { fetchWeldTelemetrySamples } from './weldTelemetryClient'

/** Mapped pass labels from persisted weldparams aggregate (for sample fetch). */
export function passLabelsForFsTimesImport(
  getSection: (key: string) => unknown,
  weldRowId: string
): string[] {
  const labels: string[] = []
  const seen = new Set<string>()
  const add = (raw: string) => {
    const pass = raw.trim()
    if (!pass || seen.has(pass)) return
    seen.add(pass)
    labels.push(pass)
  }

  const data = getSection(`weldparams.${weldRowId}`)
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const aggregate = record.aggregate
    if (aggregate && typeof aggregate === 'object') {
      const rows = (aggregate as Record<string, unknown>).rows
      if (Array.isArray(rows)) {
        for (const row of rows) {
          if (!row || typeof row !== 'object') continue
          add(String((row as Record<string, unknown>).pass ?? ''))
        }
      }
    }
    const mappings = record.passMappings
    if (mappings && typeof mappings === 'object' && !Array.isArray(mappings)) {
      for (const value of Object.values(mappings as Record<string, unknown>)) {
        add(String(value ?? ''))
      }
      for (const key of Object.keys(mappings as Record<string, unknown>)) {
        // Torch-aware keys are `Lead:RT` — also try the bare zone token.
        const bare = key.includes(':') ? key.slice(key.lastIndexOf(':') + 1) : key
        add(bare)
      }
    }
  }

  // Always probe common Root / Hot labels in case aggregate omitted them
  // but samples still exist under WPS or raw Data360 zone names.
  for (const candidate of [
    'Root',
    'Root RC',
    'Root Misfire',
    'Root Misfire 1',
    'Root Misfire 2',
    'RT',
    'RM',
    'RO',
    'RB',
    'Hot',
    'HO',
    'HB',
    'HT'
  ]) {
    add(candidate)
  }

  return labels
}

/** Fetch telemetry samples and derive fsTimes without writing sections. */
export async function deriveData360FsTimesFromTelemetry(input: {
  projectId: string
  pipeSetId: string
  weldRowId: string
  passLabels: string[]
}): Promise<DeriveFloorSheetFsTimesResult | null> {
  const { projectId, pipeSetId, weldRowId, passLabels } = input
  if (passLabels.length === 0) return null

  const samples: Array<{ passMapped: string; recordedAt: string | null }> = []
  await Promise.all(
    passLabels.map(async (passLabel) => {
      try {
        const result = await fetchWeldTelemetrySamples({
          projectId,
          pipeSetId,
          weldId: weldRowId,
          passLabel
        })
        for (const sample of result.samples) {
          samples.push({
            passMapped: passLabel,
            recordedAt: sample.recordedAt
          })
        }
      } catch {
        // Pass may have zero samples; skip.
      }
    })
  )

  const derived = deriveFloorSheetFsTimesFromSamples(samples)
  if (!derived.hasAny) return null
  return derived
}

export async function applyData360FsTimesToFloorsheet(input: {
  projectId: string
  pipeSetId: string
  weldRowId: string
  passLabels: string[]
  getSection: (key: string) => unknown
  updateSection: (key: string, value: unknown) => Promise<unknown>
}): Promise<{ applied: boolean; fsTimes?: FloorSheetFsTimes }> {
  const { projectId, pipeSetId, weldRowId, passLabels, getSection, updateSection } = input
  const derived = await deriveData360FsTimesFromTelemetry({
    projectId,
    pipeSetId,
    weldRowId,
    passLabels
  })
  if (!derived) return { applied: false }

  const floorsheet = getSection('floorsheet')
  const fsRecord =
    floorsheet && typeof floorsheet === 'object' ? (floorsheet as Record<string, unknown>) : {}
  const welds = Array.isArray(fsRecord.welds) ? (fsRecord.welds as Record<string, unknown>[]) : []
  if (welds.length === 0) return { applied: false }

  let applied = false
  const nextWelds = welds.map((w) => {
    const id = typeof w.id === 'string' ? w.id : ''
    if (id !== weldRowId) return w
    const existingSource = w.fsTimesSource === 'data360-logs' ? 'data360-logs' : null
    const existingTimes = normalizeFsTimes(w.fsTimes)
    const overwrite = existingSource === 'data360-logs'
    const nextTimes = mergeDerivedFsTimes(existingTimes, derived.fsTimes, overwrite)
    applied = true
    return {
      ...w,
      fsTimes: nextTimes,
      fsTimesSource: 'data360-logs' as const,
      fsTimesLogSnapshot: derived.snapshot as FloorSheetFsTimesLogSnapshot
    }
  })

  if (!applied) return { applied: false }

  await updateSection('floorsheet', {
    ...fsRecord,
    welds: nextWelds
  })
  return { applied: true, fsTimes: derived.fsTimes }
}
